import type { StepData } from "@/types";
import Icon from "@/components/Icon";

interface Props {
  steps: StepData[];
  activeIndex: number;
  onSelect: (index: number) => void;
  onBack: () => void;
}

/** Tiroir latéral (comportement « push »). En-tête de marque, puis le retour aux
 *  exercices et la navigation entre étapes. La bascule d'ouverture/fermeture vit
 *  désormais dans la barre du haut (voir Exercise), jamais ici, pour éviter tout
 *  saut vertical du bouton.
 *  États d'étape : validée (✓ vert), active (bordure bleue), verrouillée (séquentiel). */
export default function StepNav({ steps, activeIndex, onSelect, onBack }: Props) {
  // Apprentissage séquentiel : accès jusqu'à la première étape non validée.
  const firstIncomplete = steps.findIndex((s) => !s.progress);
  const frontier = firstIncomplete === -1 ? steps.length - 1 : firstIncomplete;

  return (
    <nav style={styles.drawer} aria-label="Navigation de l'exercice">
      <button style={styles.back} onClick={onBack}>
        <Icon name="back" size={16} style={{ color: "var(--text-muted)" }} />
        Retour aux séries
      </button>

      <span style={styles.title}>Exercices</span>
      <div style={styles.list}>
        {steps.map((s, i) => {
          const done   = !!s.progress;
          const active = i === activeIndex;
          const locked = i > frontier;
          return (
            <button
              key={s.id}
              onClick={() => !locked && onSelect(i)}
              disabled={locked}
              aria-current={active ? "step" : undefined}
              style={{
                ...styles.row,
                ...(active ? styles.rowActive : {}),
                ...(locked ? styles.rowLocked : {}),
              }}
            >
              <span style={{ ...styles.indicator, ...(done ? styles.indicatorDone : {}) }}>
                {done ? (
                  <Icon name="done" size={13} />
                ) : locked ? (
                  <Icon name="locked" size={12} style={{ color: "var(--text-muted)" }} />
                ) : (
                  i + 1
                )}
              </span>
              <span style={{ ...styles.label, ...(done && !active ? styles.labelDone : {}) }}>
                {s.title}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}

const styles: Record<string, React.CSSProperties> = {
  drawer: {
    width: 240,
    flexShrink: 0,
    height: "100%",
    background: "var(--bg-bar)",
    borderRight: "1px solid var(--border-soft)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  back: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    width: "100%",
    textAlign: "left",
    padding: "12px 14px",
    background: "transparent",
    border: "none",
    borderBottom: "1px solid var(--border-soft)",
    color: "var(--text)",
    fontSize: 13,
    fontWeight: 600,
  },
  title: {
    color: "var(--text-muted)",
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.6,
    padding: "14px 14px 6px",
  },
  list: { flex: 1, overflowY: "auto", paddingBottom: 6 },
  row: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    width: "100%",
    textAlign: "left",
    padding: "10px 14px",
    background: "transparent",
    border: "none",
    borderLeft: "3px solid transparent",
    color: "var(--text)",
    fontSize: 13,
  },
  rowActive: {
    background: "var(--bg-panel)",
    borderLeft: "3px solid var(--blue)",
    fontWeight: 600,
  },
  rowLocked: {
    // Plus foncé que --text-faint pour la lisibilité (accessibilité), tout en
    // restant visuellement non cliquable (disabled + cursor default).
    color: "var(--text-muted)",
    cursor: "default",
  },
  indicator: {
    width: 20,
    height: 20,
    borderRadius: "50%",
    background: "var(--border)",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
    flexShrink: 0,
  },
  indicatorDone: {
    background: "var(--ok-box-bg)",
    color: "var(--green)",
  },
  label: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  labelDone: { color: "var(--text-muted)" },
};
